/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CollectionPanel, ScorePanel, SmsButton } from '@/components/DebtInsights';
import { GenerateDoc } from '@/components/GenerateDoc';
import { Badge, Button, ErrorText, Field, Input, KV, Modal, PageHeader, Select, Spinner } from '@/components/ui';
import { api, download } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtMoney, today } from '@/lib/format';
import { DebtForm, InterestTable, MODE_LABEL, useStages } from './Debts';

export function DebtCardPage() {
  const { id } = useParams<{ id: string }>();
  const { can, hasModule } = useAuth();
  const qc = useQueryClient();
  const { data: stages } = useStages();
  const [on, setOn] = useState(today());
  const [edit, setEdit] = useState(false);
  const [pay, setPay] = useState({ date: today(), amount: '', kind: 'PAYMENT', note: '' });
  const { data: d, isLoading, error } = useQuery({ queryKey: ['debt', id, on], queryFn: () => api.get(`/debts/${id}?on=${on}`) });
  const inv = () => { qc.invalidateQueries({ queryKey: ['debt', id] }); qc.invalidateQueries({ queryKey: ['debts'] }); };
  const stage = useMutation({ mutationFn: (s: string) => api.patch(`/debts/${id}`, { stage: s }), onSuccess: inv });
  const addEv = useMutation({ mutationFn: () => api.post(`/debts/${id}/events`, { ...pay, amount: pay.amount.replace(',', '.') }), onSuccess: () => { setPay({ ...pay, amount: '', note: '' }); inv(); } });
  const delEv = useMutation({ mutationFn: (eid: string) => api.del(`/debts/${id}/events/${eid}`), onSuccess: inv });
  if (isLoading) return <Spinner />;
  if (error || !d) return <ErrorText error={error} />;
  const s = d.summary; const w = can('debt.write');
  return (
    <>
      <PageHeader title={d.debtor.name} subtitle={<>{d.basis} · <Badge tone="blue">{stages?.find((x) => x.code === d.stage)?.name}</Badge></>}
        actions={<>
          <Button variant="secondary" onClick={() => download(`/debts/${id}/calc.xlsx?on=${on}`)}><FileSpreadsheet size={16} />Расчёт в Excel</Button>
          {hasModule('M25') && can('party.write') && <SmsButton partyId={d.debtor.id} debtId={id} />}
          <GenerateDoc entity="debt" entityId={id!} />
          {w && <Button variant="secondary" onClick={() => setEdit(true)}>Изменить</Button>}
        </>} />
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[['Основной долг', s.principalBalance], ['Проценты / неустойка', s.interest], ['Погашено', s.paid], ['Всего к взысканию', s.total]].map(([k, v]) => (
          <div key={k} className="card p-4"><div className="text-xs text-slate-500">{k}</div><div className="mt-1 text-lg font-semibold tabular-nums">{fmtMoney(v)}</div></div>
        ))}
      </div>
      <div className="mb-5 grid gap-5 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-2">
          <KV items={[
            ['Должник', <Link key="d" className="text-brand-700 hover:underline" to={`/parties/${d.debtor.id}`}>{d.debtor.name}{d.debtor.inn ? `, ИНН ${d.debtor.inn}` : ''}</Link>],
            ['Кредитор', d.creditor?.name], ['Сумма по основанию', fmtMoney(d.principal)], ['Первый день просрочки', fmtDate(d.dueDate)],
            ['Начисление', `${MODE_LABEL[d.penaltyMode]}${d.penaltyRate ? `, ${d.penaltyRate}%` : ''}${d.penaltyCap ? `, не более ${fmtMoney(d.penaltyCap)}` : ''}`],
            ['Исковая давность до', s.limitationExpired ? <Badge key="l" tone="red">истекла {fmtDate(s.limitationEnd)}</Badge> : fmtDate(s.limitationEnd)],
            ['Реестр', d.portfolio], ['Заметки', d.note],
          ]} />
        </div>
        <div className="card space-y-3 p-4">
          <Field label="Этап взыскания"><Select disabled={!w} value={d.stage} onChange={(e) => stage.mutate(e.target.value)} options={(stages ?? []).map((x) => ({ value: x.code, label: x.name }))} /></Field>
          <Field label="Расчёт на дату"><Input type="date" value={on} onChange={(e) => setOn(e.target.value || today())} /></Field>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-2">
          <div className="mb-2 text-sm font-medium">Расчёт на {fmtDate(s.on)}</div>
          {s.rows.length ? <InterestTable rows={s.rows} total={s.interest} daily={d.penaltyMode === 'FIXED_DAILY'} /> : <div className="text-sm text-slate-500">Начислений нет</div>}
          {s.interestCapped && <div className="mt-2 text-xs text-amber-700">Сумма ограничена договором</div>}
        </div>
        <div className="card p-4">
          <div className="mb-2 text-sm font-medium">Оплаты и доначисления</div>
          <ul className="mb-3 divide-y divide-slate-100 text-sm">
            {d.events.map((e: any) => (
              <li key={e.id} className="flex items-start justify-between gap-2 py-1.5">
                <span className="min-w-0">{fmtDate(e.date)} {e.note && <span className="block text-xs text-slate-500">{e.note}</span>}</span>
                <span className={`shrink-0 whitespace-nowrap tabular-nums ${Number(e.amount) < 0 ? 'text-green-700' : ''}`}>{fmtMoney(e.amount)}{w && <button className="ml-2 text-xs text-slate-400 hover:text-red-600" onClick={() => delEv.mutate(e.id)}>×</button>}</span>
              </li>
            ))}
            {!d.events.length && <li className="py-1.5 text-slate-500">Пока нет</li>}
          </ul>
          {w && <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2"><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /><Input placeholder="Сумма" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
            <Select value={pay.kind} onChange={(e) => setPay({ ...pay, kind: e.target.value })} options={[{ value: 'PAYMENT', label: 'Оплата (уменьшает долг)' }, { value: 'ACCRUAL', label: 'Новый долг (увеличивает)' }]} />
            <Input placeholder="Комментарий" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} />
            <ErrorText error={addEv.error} />
            <Button size="sm" disabled={!pay.amount || addEv.isPending} onClick={() => addEv.mutate()}>Добавить</Button>
          </div>}
        </div>
      </div>
      {(hasModule('M19') || hasModule('M23')) && <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {hasModule('M19') && <ScorePanel debtId={id!} />}
        {hasModule('M23') && <CollectionPanel debtId={id!} debtorPerson={d.debtor.kind === 'PERSON'} />}
      </div>}
      <Modal title="Изменение долга" open={edit} onClose={() => setEdit(false)} wide><DebtForm debt={d} onSaved={() => setEdit(false)} /></Modal>
    </>
  );
}
